import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { ActionsModal, type Action } from './ActionsModal.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';
import { Pagination } from './Pagination.js';
import { Toolbar, type ToolbarSearch } from './Toolbar.js';
import { useDragScroll } from './useDragScroll.js';
import { useOverflow } from './useOverflow.js';

/** Onde o conteúdo encosta dentro da célula. */
export type Alignment = 'inicio' | 'centro' | 'fim';

export interface Column<T> {
  title: string;
  cell: (item: T) => ReactNode;
  /**
   * **Número e ação alinham à direita; o resto começa onde o texto começa.**
   *
   * Selo de situação NÃO é nenhum dos dois, e três colunas o alinhavam à
   * direita: como o selo é pílula de largura variável, os símbolos ficavam em
   * posições diferentes a cada linha e saber quais linhas estão concluídas
   * exigia ler, não olhar. O `Badge` existe para o símbolo carregar o estado —
   * uma coluna que desalinha os símbolos gasta exatamente o que ele entrega.
   *
   * **O padrão é `centro` desde 17/09**, e era `inicio`. O desenho centra tudo,
   * e a razão aparece numa tabela de nove colunas: com o dado curto encostado à
   * esquerda, cada célula fica com um vão à direita que a separa da borda da
   * coluna, e a grade passa a parecer torta.
   *
   * `inicio` fica para o que a referência também deixa à esquerda: **nome e
   * prosa** — cliente, consultor, título, descrição. Texto de comprimento
   * variável centrado perde a margem esquerda comum, que é a linha por onde o
   * olho desce a coluna, e é justamente nessas que se lê palavra por palavra.
   */
  alignment?: Alignment;
  /**
   * O alinhamento do CABEÇALHO, quando ele não é o da célula.
   *
   * O padrão centra — inclusive sobre coluna alinhada à esquerda, que é o caso
   * do título do registro: o rótulo "Título" centrado sobre textos que começam
   * na margem esquerda é o desenho de 17/09, e a razão é que o cabeçalho
   * nomeia a COLUNA, enquanto a célula carrega o dado. Um rótulo curto colado
   * na esquerda de uma coluna larga parece pertencer à coluna anterior.
   *
   * A exceção é `fim`: ali o cabeçalho acompanha a célula. Numa coluna de
   * dinheiro, o número se lê pela direita, e um rótulo centrado tira a marca de
   * onde a leitura começa.
   */
  headerAlignment?: Alignment;
  /** Para a coluna de ação, que não tem título visível mas precisa de nome. */
  hiddenLabel?: string;
  minWidth?: string;
  /**
   * Largura declarada da coluna, como no desenho: `'56px'`, `'12rem'`.
   *
   * É uma DICA, não uma promessa: a tabela continua com `table-layout: auto`,
   * então o navegador pode alargar a coluna para caber o conteúdo. Fixar o
   * layout entregaria as larguras do desenho ao preço de cortar nome próprio em
   * toda linha — e nome cortado numa lista de associados é a coluna deixando de
   * servir para o que existe.
   */
  width?: string;
  /**
   * Ordenar NO NAVEGADOR: o valor pelo qual esta coluna se ordena.
   *
   * Só faz sentido quando a tabela tem a lista INTEIRA na mão. Quem pagina no
   * servidor usa `sortKey` com `sort`/`onSortChange`, senão a tela ordena as 50
   * linhas da página e diz, pelo símbolo aceso no cabeçalho, que ordenou as
   * 76.745.
   */
  sortValue?: (item: T) => string | number | null | undefined;
  /**
   * Ordenar NO SERVIDOR: o nome que a rota conhece. A tabela não reordena nada
   * — só acende o cabeçalho e avisa por `onSortChange`.
   */
  sortKey?: string;
  /**
   * A célula desta coluna na linha de TOTAL, no pé da tabela — a premiação da
   * cobrança, que soma o recuperado e a bonificação da equipe como o painel do
   * legado (29/10/2026).
   *
   * **Quem soma é quem chama**, e sobre a lista INTEIRA: a tabela não sabe
   * somar `Money`, e somar só as linhas da página mostraria um total que muda
   * ao virar a página. Por isso a linha de total pede `paginate={false}` ou um
   * total que já venha do servidor.
   *
   * Basta UMA coluna declarar para o rodapé existir; as outras ficam com a
   * célula vazia. Nenhuma declarando, não há rodapé.
   */
  footer?: ReactNode;
}

export type SortDirection = 'ascending' | 'descending';

export interface Sort {
  key: string;
  direction: SortDirection;
}

/*
 * As chaves são os VALORES de `Alignment`, e não nomes nossos — 28 chamadas
 * escrevem `alignment: 'fim'`, e traduzi-las quebra a indexação junto com elas.
 */
// @valor
const ALIGN: Record<Alignment, string> = {
  inicio: 'text-left',
  centro: 'text-center',
  fim: 'text-right',
};

/**
 * O alvo quadrado dentro da linha — abrir, expandir, agir.
 *
 * `pointer-coarse:size-11` é a régua da casa, e ela ganha de qualquer medida do
 * desenho: um alvo de 24px no dedo é o defeito que `touch.test.ts` cobra em
 * todo botão, e não haveria por que esta peça ser a exceção só por estar dentro
 * de uma célula. O tamanho no mouse vem de quem usa — 24px no `+`, 32px no
 * gatilho de ações e na seta, que são ícone sozinho sem moldura nem rótulo.
 *
 * **Nenhum destes botões desabilita**, e é decisão: o `+` está sempre
 * disponível, o cabeçalho ordenável está sempre ordenável, e o gatilho de ações
 * não é desenhado quando a linha não tem ação — em vez de aparecer cinzento
 * abrindo um painel vazio. Está declarado em `statuses.test.ts`, que cobra o
 * estado "desabilitado" de todo componente com botão e aceita a ausência só com
 * o motivo escrito nos dois lugares.
 */
const CELL_TARGET =
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-pequeno ' +
  'transition-colors active:scale-[0.94] focus-visible:outline-2 ' +
  'focus-visible:outline-offset-2 focus-visible:outline-foco pointer-coarse:size-11';

/**
 * As classes da seta que abre a linha, para quem monta o link.
 *
 * Ela não vem pronta porque o `Link` é do roteador do aplicativo, e este pacote
 * serve as DUAS aplicações e não conhece nenhum dos dois. Um `<a href>` aqui
 * dentro daria recarga de página inteira numa SPA — a versão silenciosa do
 * defeito, que não quebra teste nenhum e só aparece cronometrando.
 */
export const rowLinkClasses = cn(
  CELL_TARGET,
  /* Mesma medida do gatilho de ações: os dois são ícone sozinho na última
     coluna, e dois tamanhos ali seriam duas alturas de linha. */
  'size-8 text-texto-suave hover:bg-superficie-suave hover:text-texto',
);

/**
 * O cabeçalho de uma coluna ordenável.
 *
 * O símbolo é UM (`ordenar`), e não duas setas empilhadas: em 13px duas setas
 * nossas viram um borrão, e são dois alvos visuais para uma ação só. Aceso, ele
 * troca para a seta do sentido — quem já ordenou precisa saber por qual coluna
 * e para que lado, e a cor sozinha não diz o lado.
 */
function SortButton({
  title,
  active,
  direction,
  onToggle,
}: {
  title: string;
  active: boolean;
  direction: SortDirection;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pequeno transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
        'hover:text-texto',
        active ? 'text-texto' : 'text-texto-suave',
      )}
    >
      {title}
      <Icon
        nome={
          active ? (direction === 'ascending' ? 'seta-cima' : 'seta-baixo') : 'ordenar'
        }
        size={16}
        className={cn('text-primaria-texto', !active && 'opacity-70')}
      />
    </button>
  );
}

/**
 * Tabela com cabeçalho ligado às células.
 *
 * ## O que ela é, desde 17/09
 *
 * É a ÚNICA tabela das duas aplicações, e por isso mora em `@evogard/ui` e não
 * em `apps/web`. Antes ela era da interna, e o portal — que vai ter a lista de
 * boletos — teria que copiá-la ou inventar a segunda. Duas tabelas são duas
 * ideias de quanto respira uma linha, e a medição de 01/09 já mostrou o que
 * isso custa: seis larguras de conteúdo em doze rotas, sem nenhum teste pegando,
 * porque cada tela isolada estava certa.
 *
 * O desenho é o de 17/09: cabeçalho tingido, grade de colunas, linha alta, e a
 * coluna de ações à direita com o gatilho de reticências. **A grade veio de
 * volta**, e é a única decisão aqui que desfaz uma anterior: 06/09 tirou as
 * bordas verticais porque "trinta traços do mesmo peso competem com o dado". A
 * de agora as traz com peso MENOR que a borda da linha, que é o que separa uma
 * grade legível de um quadriculado.
 *
 * ## Os estados são três, e nunca dois
 *
 * Vazio, carregando e cheio. Enquanto `loading` não existia, quem chamava
 * passava `items={consulta.data ?? []}` e a tabela anunciava "Nenhum usuário
 * nesta base" durante o carregamento — uma tela afirmando um fato que o código
 * ainda não sabia.
 *
 * ## Ordenar tem dois modos, e misturá-los mente
 *
 * `sortValue` ordena no navegador e só serve a quem tem a lista inteira.
 * `sortKey` + `sort`/`onSortChange` delega a quem responde. Uma tabela que
 * pagina no servidor e ordena no navegador acende o cabeçalho sobre as 50
 * linhas da página, e quem lê entende que ordenou as 76.745.
 */
/**
 * A fatia de uma página — a conta da paginação no navegador, fora do componente
 * para o teste a exercitar sem DOM.
 *
 * A página pedida é LIMITADA ao total, e não corrigida no estado: filtrar a
 * lista encurta o total, e quem estava na página 7 de 12 ficaria numa página
 * que deixou de existir. Página inválida (zero, negativa, `NaN` de uma URL
 * torta) é a primeira.
 */
export function pageSlice<T>(
  items: readonly T[],
  page: number,
  perPage: number,
): { current: number; pages: number; shown: T[] } {
  const size = Number.isFinite(perPage) && perPage >= 1 ? Math.floor(perPage) : 10;
  const pages = Math.max(1, Math.ceil(items.length / size));
  const asked = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  const current = Math.min(asked, pages);
  return { current, pages, shown: items.slice((current - 1) * size, current * size) };
}

export function Table<T>({
  columns,
  items,
  rowKey,
  label,
  empty,
  loading = false,
  skeletonRows = 3,
  rowLink,
  rowActions,
  rowTitle,
  rowSubtitle,
  rowIcon,
  onRowActions,
  expand,
  expanded,
  onExpandedChange,
  expandOnRowClick = false,
  onRowClick,
  rowHighlight,
  search,
  toolbar,
  framed = false,
  paginate = true,
  defaultPerPage = 10,
  page: controlledPage,
  onPageChange,
  perPage: controlledPerPage,
  onPerPageChange,
  sort: controlledSort,
  onSortChange,
}: {
  columns: Array<Column<T>>;
  items: T[];
  /**
   * Nome não pode ser `key`: em JSX, `key` é atributo reservado do React e
   * qualquer componente cujo tipo de props declare um campo `key` sofre a
   * colisão que o TS relata como interseção `(Key | null) & (item: T) => string`.
   */
  rowKey: (item: T) => string;
  /**
   * O que esta tabela lista, em uma frase curta: "Usuários desta base".
   *
   * Vira `<caption>` invisível e nome da região rolável. É obrigatório porque
   * um leitor de tela anunciando "tabela, 6 colunas, 30 linhas" sem dizer de
   * quê obriga a pessoa a entrar e ler o cabeçalho para descobrir onde está —
   * e porque uma região focável sem nome é uma parada de teclado anônima.
   */
  label: string;
  /** Ensina a tela: diz o que apareceria aqui, não que não há nada. */
  empty: string;
  loading?: boolean;
  skeletonRows?: number;
  /**
   * A seta que abre a linha — o ELEMENTO, montado por quem chama.
   *
   * Ela devolve um nó e não `{ to, label }` porque este pacote não conhece o
   * roteador de nenhuma das duas aplicações, e um `<a href>` numa SPA recarrega
   * a página inteira. Quem chama monta o `Link` do seu roteador com
   * `rowLinkClasses` e o ícone `seta-direita`, e a tabela só lhe dá a coluna.
   *
   * A linha inteira **não** vira clicável, ao contrário do desenho. Uma `<tr>`
   * com `onClick` não é alcançável por teclado, engole a seleção de texto da
   * célula e, com um link dentro (o código da demanda), produz dois destinos no
   * mesmo alvo. A seta é a versão honesta da mesma affordance.
   */
  rowLink?: (item: T) => ReactNode;
  /**
   * O que esta linha permite fazer. Quando existe, nasce a coluna de ações com
   * o gatilho de reticências do desenho.
   *
   * Devolver lista VAZIA é resposta legítima e a tabela a respeita: a linha
   * fica sem gatilho, em vez de oferecer um botão que abre um painel vazio. É
   * o que acontece por permissão — quem só enxerga recebe a lista vazia da
   * mesma função que monta as ações de quem pode.
   */
  rowActions?: (item: T) => Action[];
  /** O nome da linha no painel de ações: "Demanda EV-QDJV". Sem ele, o `label`. */
  rowTitle?: (item: T) => string;
  /** A segunda linha do cabeçalho do painel: "ID: 17089". */
  rowSubtitle?: (item: T) => string;
  /** O ladrilho do cabeçalho do painel. */
  rowIcon?: IconName;
  /**
   * Quem ABRE o painel de ações, quando não se quer o desta peça.
   *
   * Existe para a tela que precisa de outro painel que não o `ActionsModal` —
   * e nenhuma precisa hoje. Sem ele, a tabela abre o da casa.
   */
  onRowActions?: (item: T, actions: Action[]) => void;
  /**
   * O que a linha esconde. Quando existe, nasce a coluna do `+` à esquerda.
   *
   * Uma linha por vez fica aberta. Duas seriam o começo de um acordeão, e um
   * acordeão dentro de tabela é o desenho pedindo para virar detalhe de
   * registro.
   */
  expand?: (item: T) => ReactNode;
  /**
   * O clique em QUALQUER ponto da linha abre e fecha o `expand` — o desenho da
   * Agenda de clientes (21/10/2026), em que a pessoa lê a lista e abre o
   * cliente ali mesmo.
   *
   * Não contradiz o que `rowLink` diz da `<tr>` clicável, porque o que ela
   * resolve é outro: aqui não há destino, só um painel que abre no lugar. O
   * teclado continua no `+`, que segue lá; o clique que nasce de um controle
   * da linha (link, botão) é dele e não abre nada; e quem arrasta para
   * selecionar o texto da célula não abre a linha ao soltar.
   */
  expandOnRowClick?: boolean;
  /**
   * A linha aberta, controlada por quem desenha a tabela — a `rowKey` dela, ou
   * `null`. Sem o campo, a tabela guarda sozinha.
   *
   * Existe porque um botão DA LINHA pode querer abrir o painel: "Pagar", em
   * Pagamentos aos prestadores (22/10/2026), mudava o próprio texto para
   * "Fechar" e o formulário não aparecia, porque quem abria a linha era só o
   * `+`. Com o par controlado, o botão e o `+` mexem no mesmo estado.
   */
  expanded?: string | null;
  onExpandedChange?: (key: string | null) => void;
  /**
   * O clique em QUALQUER ponto da linha leva ao destino dela — a tabela de
   * veículos da Agenda de clientes (22/10/2026), em que a pessoa mirava a placa
   * para abrir o carro.
   *
   * As guardas são as do `expandOnRowClick`: o teclado continua no controle
   * que a célula já tem (a placa, que segue sendo botão), o clique que nasce de
   * um controle da linha é dele, e quem arrasta para selecionar texto não
   * dispara nada. Com `expand` e `expandOnRowClick` juntos, quem vence é o
   * `expand`.
   */
  onRowClick?: (item: T) => void;
  /**
   * A linha em DESTAQUE — os boletos não pagos, quando a pessoa clica no
   * número deles na ficha do carro (22/10/2026). É fundo de alerta, e só fundo:
   * a tabela continua a mesma, na mesma ordem, e o que não casa fica onde está.
   */
  rowHighlight?: (item: T) => boolean;
  /** A busca da barra de cima. Quem filtra é quem chama — a tabela só desenha. */
  search?: ToolbarSearch;
  /** Os botões da barra de cima: "Nova vistoria", "Filtros", "Exportar". */
  toolbar?: ReactNode;
  /**
   * Contorno próprio. O padrão é `false` porque a tabela quase sempre mora
   * dentro de `<Card bleed>`, e ali quem contorna é o cartão — com os dois, o
   * canto arredondado do cartão ganha um canto reto por dentro.
   */
  framed?: boolean;
  /**
   * `false` para a tela que pagina NO SERVIDOR e desenha o próprio paginador.
   *
   * Sem isto, a lista de 76.745 veículos seria repartida duas vezes: a API
   * manda 50 linhas e a tabela mostraria 25 delas, com o paginador de baixo
   * dizendo "1 a 25 de 50" sobre um total que é outro. Quem já pagina de
   * verdade desliga o de casa.
   */
  paginate?: boolean;
  /** Quantas linhas a página tem antes de alguém mexer no seletor — dez, por decisão de 15/09. */
  defaultPerPage?: number;
  /**
   * A página ATUAL, quando quem a guarda é a tela — na URL, para o "voltar" do
   * navegador devolver a pessoa onde ela estava (19/10/2026). Sem `onPageChange`
   * a tabela guarda a página sozinha, como sempre.
   */
  page?: number;
  onPageChange?: (page: number) => void;
  /** O tamanho da página, nas mesmas condições de `page`. */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  /** A ordenação ATUAL, quando quem ordena é o servidor. */
  sort?: Sort | null;
  /** Quando existe, a tabela não reordena nada: só avisa. */
  onSortChange?: (sort: Sort | null) => void;
}) {
  const { ref, overflows } = useOverflow('horizontal');
  useDragScroll(ref);
  const [localPage, setLocalPage] = useState(1);
  const [localPerPage, setLocalPerPage] = useState(defaultPerPage);
  /* controlada só quando a tela também ouve a troca — sem ouvir, seria uma página que não anda */
  const page = onPageChange !== undefined ? (controlledPage ?? 1) : localPage;
  const perPage =
    onPerPageChange !== undefined ? (controlledPerPage ?? defaultPerPage) : localPerPage;
  const setPage = (n: number) => (onPageChange ? onPageChange(n) : setLocalPage(n));
  const setPerPage = (n: number) =>
    onPerPageChange ? onPerPageChange(n) : setLocalPerPage(n);
  const [localSort, setLocalSort] = useState<Sort | null>(null);
  const [localOpened, setLocalOpened] = useState<string | null>(null);
  const opened = expanded !== undefined ? expanded : localOpened;
  const setOpened = (key: string | null) => {
    if (expanded === undefined) setLocalOpened(key);
    onExpandedChange?.(key);
  };
  const [actionsOf, setActionsOf] = useState<T | null>(null);

  const controlled = onSortChange !== undefined;
  const sort = controlled ? (controlledSort ?? null) : localSort;

  /*
   * A chave de ordenação de uma coluna, e de qual dos dois modos ela é.
   *
   * `sortKey` ganha de `sortValue` quando os dois existem: declarar os dois é
   * pedir ao servidor e reordenar de novo aqui, e o que se veria na tela seria
   * a segunda ordenação — a das 50 linhas da página.
   */
  const keyOf = (c: Column<T>) => c.sortKey ?? (c.sortValue ? c.title : undefined);

  const ordered = useMemo(() => {
    if (controlled || !sort) return items;
    const column = columns.find(
      (c) => (c.sortKey ?? (c.sortValue ? c.title : undefined)) === sort.key,
    );
    const value = column?.sortValue;
    if (!value) return items;
    const factor = sort.direction === 'ascending' ? 1 : -1;
    return [...items].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      /*
       * Ausência vai para o FIM nos dois sentidos, e não para o começo do
       * descendente. "Sem placa" não é o maior valor da coluna — é a falta
       * dela, e pôr as ausências no topo ao inverter a ordem faz a primeira
       * página de uma lista ordenada por placa não ter placa nenhuma.
       */
      const noLeft = left === null || left === undefined || left === '';
      const noRight = right === null || right === undefined || right === '';
      if (noLeft || noRight) return noLeft && noRight ? 0 : noLeft ? 1 : -1;
      if (typeof left === 'number' && typeof right === 'number')
        return (left - right) * factor;
      return (
        String(left).localeCompare(String(right), 'pt-BR', { numeric: true }) * factor
      );
    });
  }, [columns, controlled, items, sort]);

  /*
   * A página é CORRIGIDA no cálculo, e não guardada corrigida.
   *
   * Filtrar a lista encurta o total, e quem estava na página 7 de 12 ficaria
   * numa página que deixou de existir — uma tabela vazia sobre um total que diz
   * haver linhas. Um `useEffect` que chamasse `setPage` resolveria com uma
   * renderização a mais e uma piscada de tabela vazia no meio; derivar resolve
   * na mesma renderização. O estado guarda a intenção, e a conta a limita.
   */
  const slice = pageSlice(ordered, page, perPage);
  const current = slice.current;
  const shown = paginate ? slice.shown : ordered;

  function toggleSort(key: string) {
    const next: Sort | null =
      sort?.key !== key
        ? { key, direction: 'ascending' }
        : sort.direction === 'ascending'
          ? { key, direction: 'descending' }
          : /* O terceiro clique DESORDENA, e devolve a ordem que a rota deu.
               Sem ele não há como desfazer — e "ordem original" é informação:
               é ela que põe o registro mais novo em cima. */
            null;
    if (controlled) onSortChange?.(next);
    else setLocalSort(next);
    setPage(1);
  }

  /*
   * As ações de cada linha visível, calculadas UMA vez.
   *
   * Duas coisas dependem delas — se a coluna existe e o que cada célula
   * desenha —, e `rowActions` é do chamador: nada promete que ele seja barato
   * nem que responda a mesma coisa duas vezes seguidas. É a mesma disciplina
   * que o comentário de `rowLink` já declara alguns blocos abaixo.
   */
  const actionsByRow: Map<string, Action[]> | null = rowActions
    ? new Map(shown.map((item) => [rowKey(item), rowActions(item)]))
    : null;

  /**
   * A coluna "Ações" só existe quando ALGUMA linha visível tem ação.
   *
   * ## O que ela consertou, em 21/09/2026
   *
   * Bastava passar `rowActions` para a coluna ser desenhada — e ela ficava
   * lá, com o cabeçalho "Ações" e todas as células vazias, para quem não tem
   * permissão de agir sobre nada daquela tabela. O quadro afirmava que há o
   * que fazer ali e que quem olha não conseguiu descobrir como.
   *
   * Quem devolve `[]` por linha já estava certo: o ranking documenta, no
   * próprio `rowActions`, que "devolver lista vazia é a resposta certa". O que
   * faltava era a tabela ouvir a resposta.
   *
   * **Por linha visível, e não pelo conjunto inteiro:** a régua do ranking
   * decide por linha (quem enxerga só o próprio time age nas linhas dele e em
   * nenhuma outra), então uma página sem nenhuma linha alcançável não deve
   * mostrar a coluna, mesmo que a página seguinte mostre. A alternativa —
   * decidir sobre `ordered` — faria a coluna aparecer vazia numa página
   * inteira.
   */
  const showActions =
    actionsByRow !== null && [...actionsByRow.values()].some((a) => a.length > 0);

  /* As colunas extras: o `+` à esquerda, a seta e as reticências à direita. */
  const leading = expand ? 1 : 0;
  const trailing = (rowLink ? 1 : 0) + (showActions ? 1 : 0);
  const span = columns.length + leading + trailing;

  /* A linha de total existe quando alguma coluna a declara — ver `Column.footer`. */
  const footed = columns.some((c) => c.footer !== undefined);

  /*
   * **A barra desenha o próprio recuo, como o paginador.**
   *
   * A tabela vive dentro de `<Card bleed>`, onde o corpo do cartão não tem
   * recuo nenhum — é o que faz a tabela encostar nas bordas, que é o desenho.
   * A barra herdava isso e nascia colada nas duas laterais: o campo de busca
   * começava na borda do cartão e terminava na outra, sem respiro em volta, e
   * a fileira lia como se estivesse fora do cartão em vez de dentro.
   *
   * O `border-b` fecha o par com o `border-t` do paginador: a lista fica entre
   * as duas faixas, e cada uma paga o próprio recuo. Era `mb-4`, uma margem que
   * separava sem explicar o que separava de quê.
   */
  const bar =
    search || toolbar ? (
      <Toolbar
        className="border-b border-borda px-4 py-3.5"
        search={search}
        toolbar={toolbar}
      />
    ) : null;

  if (!loading && items.length === 0) {
    return (
      <>
        {bar}
        <p className="px-4 py-10 text-center text-sm text-texto-suave">{empty}</p>
      </>
    );
  }

  /*
   * A grade: borda à direita de toda célula, menos a última.
   *
   * A última é o que impede a segunda linha por dentro do cartão — com ela, a
   * borda do `<Card>` e a da célula ficam a 1px uma da outra, e o canto
   * arredondado passa a ter um canto reto dentro.
   */
  const grid = 'border-r border-borda-suave last:border-r-0';
  const cell = 'h-14 px-3 py-2 align-middle';

  return (
    <>
      {bar}
      <div
        ref={ref}
        className={cn(
          /*
           * `relative` é o que impede a página inteira de rolar de lado, e a
           * causa é sutil o bastante para valer o comentário.
           *
           * `sr-only` é `position: absolute`. Sem ancestral posicionado, o bloco
           * que o contém é o viewport — então um rótulo escondido numa coluna
           * lá da direita (o `hiddenLabel` de uma coluna de ação, o "Abrir" do
           * `rowLink`) fica ancorado a 612px e **escapa** deste container, que
           * tem `overflow-x`. O resultado é `<html>` com 613px de rolagem sobre
           * um `<body>` de 400: a página ganha uma faixa vazia rolável ao lado
           * de uma tabela que já rolava sozinha, direitinho.
           *
           * Medido em 10/09 a 400px de largura, em `/sales/ranking` e
           * `/sales/sales`. Nenhum elemento visível estava fora do lugar — por
           * isso o defeito sobreviveu: só aparece medindo `scrollWidth`, e o
           * elemento que o causa tem 1px e é invisível.
           */
          'relative rolagem-lateral',
          framed && 'rounded-borda border border-borda',
          overflows && 'focus-visible:outline-offset-[-2px]',
        )}
        // sem transbordo não há o que rolar, e a região deixa de existir para o teclado
        {...(overflows
          ? { role: 'region' as const, 'aria-label': label, tabIndex: 0 }
          : {})}
      >
        <table className="w-full text-xs" aria-busy={loading}>
          {/*
           * A legenda existe e não aparece: ela é o nome da tabela para quem lê
           * por leitor de tela, e na tela quem já diz isso é o título do cartão
           * em volta. Desenhá-la de novo seria o mesmo texto duas vezes.
           */}
          <caption className="sr-only">{label}</caption>
          {/*
           * O cabeçalho se separa por FUNDO, e as colunas por uma borda mais
           * fraca que a da linha.
           *
           * São dois pesos e não um, e é isso que diferencia a grade do desenho
           * de 17/09 do quadriculado que 06/09 tirou daqui: `borda` fecha a
           * linha na horizontal, `borda-suave` divide as colunas na vertical.
           * Com o mesmo peso nos dois eixos, trinta traços iguais competem com o
           * dado; com dois, a linha continua sendo a unidade de leitura e a
           * coluna só ganha um trilho.
           */}
          <thead>
            <tr className="border-b border-borda bg-superficie-suave">
              {expand && (
                <th scope="col" className={cn('w-13', grid)}>
                  <span className="sr-only">Detalhar</span>
                </th>
              )}
              {columns.map((c) => {
                const key = keyOf(c);
                const active = Boolean(key && sort?.key === key);
                return (
                  <th
                    key={c.title}
                    scope="col"
                    style={{
                      ...(c.minWidth ? { minWidth: c.minWidth } : {}),
                      ...(c.width ? { width: c.width } : {}),
                    }}
                    /*
                     * `aria-sort` no `<th>`, e não no botão: quem lê por leitor
                     * de tela recebe o sentido ao entrar na COLUNA, e não só ao
                     * pousar no controle que a ordena.
                     */
                    aria-sort={active ? sort?.direction : undefined}
                    className={cn(
                      'px-3 py-3 text-[0.6875rem] font-medium text-texto-suave',
                      grid,
                      ALIGN[
                        c.headerAlignment ?? (c.alignment === 'fim' ? 'fim' : 'centro')
                      ],
                    )}
                  >
                    {c.hiddenLabel ? (
                      <span className="sr-only">{c.hiddenLabel}</span>
                    ) : key ? (
                      <SortButton
                        title={c.title}
                        active={active}
                        direction={sort?.direction ?? 'ascending'}
                        onToggle={() => toggleSort(key)}
                      />
                    ) : (
                      c.title
                    )}
                  </th>
                );
              })}
              {rowLink && (
                <th scope="col" className={cn('w-10', grid)}>
                  <span className="sr-only">Abrir</span>
                </th>
              )}
              {showActions && (
                <th
                  scope="col"
                  className={cn(
                    'w-16 px-3 py-3 text-center text-[0.6875rem] font-medium text-texto-suave',
                    grid,
                  )}
                >
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={i} className="border-b border-borda last:border-0">
                    {Array.from({ length: span }, (_, j) => (
                      <td key={j} className={cn(cell, grid)}>
                        <span
                          className="esqueleto block h-4"
                          // larguras diferentes por coluna e deslocadas a cada linha,
                          // senão o esqueleto vira uma grade de blocos idênticos e
                          // parece um erro de layout
                          style={{ width: `${[68, 84, 52][(i + j) % 3]}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : shown.map((item) => {
                  const id = rowKey(item);
                  const actions = actionsByRow?.get(id) ?? [];
                  const open = opened === id;
                  const panel = open ? expand?.(item) : null;
                  return (
                    <Fragment key={id}>
                      <tr
                        onClick={
                          (expand && expandOnRowClick) || onRowClick
                            ? (event) => {
                                const target = event.target as HTMLElement;
                                if (
                                  target.closest(
                                    'a, button, input, select, textarea, label',
                                  )
                                ) {
                                  return;
                                }
                                if (window.getSelection()?.toString()) return;
                                if (expand && expandOnRowClick) {
                                  setOpened(open ? null : id);
                                } else {
                                  onRowClick?.(item);
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          'border-b border-borda transition-colors last:border-0',
                          open
                            ? 'bg-superficie-suave'
                            : rowHighlight?.(item)
                              ? 'bg-alerta-suave'
                              : 'hover:bg-superficie-suave',
                          ((expand && expandOnRowClick) || onRowClick) &&
                            'cursor-pointer',
                        )}
                      >
                        {expand && (
                          <td className={cn(cell, 'text-center', grid)}>
                            <button
                              type="button"
                              onClick={() => setOpened(open ? null : id)}
                              aria-expanded={open}
                              aria-label={open ? 'Recolher a linha' : 'Detalhar a linha'}
                              className={cn(
                                CELL_TARGET,
                                'size-6 bg-primaria-suave text-primaria-texto hover:bg-primaria hover:text-primaria-contraste',
                              )}
                            >
                              <Icon nome={open ? 'seta-cima' : 'mais'} size={16} />
                            </button>
                          </td>
                        )}
                        {columns.map((c) => (
                          <td
                            key={c.title}
                            className={cn(
                              cell,
                              'text-texto',
                              grid,
                              ALIGN[c.alignment ?? 'centro'],
                            )}
                          >
                            {c.cell(item)}
                          </td>
                        ))}
                        {rowLink && (
                          <td className={cn(cell, 'px-2 text-center', grid)}>
                            {/* `rowLink` é chamado UMA vez por linha, e não duas
                                — uma para o destino e outra para o nome
                                acessível. Ele é do chamador, e nada promete que
                                ele seja barato nem que responda a mesma coisa
                                duas vezes seguidas. */}
                            {rowLink(item)}
                          </td>
                        )}
                        {showActions && (
                          <td className={cn(cell, 'px-2 text-center', grid)}>
                            {actions.length > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  onRowActions
                                    ? onRowActions(item, actions)
                                    : setActionsOf(item)
                                }
                                aria-label={`Ações de ${rowTitle?.(item) ?? label}`}
                                aria-haspopup="dialog"
                                className={cn(
                                  CELL_TARGET,
                                  /*
                                   * Maior e mais escuro que o resto dos alvos
                                   * de célula, e os três motivos são o mesmo:
                                   * ele é um ícone SOZINHO, sem moldura e sem
                                   * rótulo. 32px em vez de 24; `texto-suave` em
                                   * vez de `texto-tenue`; e `--traco-icone` em
                                   * 2.5, porque os três pontos são traços de
                                   * comprimento zero — a espessura do traço é a
                                   * única coisa que os faz existir, e na
                                   * espessura padrão eles sumiam.
                                   */
                                  'size-8 [--traco-icone:2.5] text-texto-suave',
                                  'hover:bg-superficie-suave hover:text-texto',
                                )}
                              >
                                <Icon nome="reticencias" size={24} />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {panel && (
                        <tr className="border-b border-borda bg-superficie-suave last:border-0">
                          <td colSpan={span} className="px-4 py-3.5">
                            {panel}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
          </tbody>
          {/*
           * O total no pé, separado do corpo pelo mesmo fundo do cabeçalho: as
           * duas faixas de fora são da tabela, e o que está entre elas é dado.
           * Some enquanto carrega — um total sobre o esqueleto seria a soma do
           * que ainda não chegou.
           */}
          {footed && !loading && (
            <tfoot>
              <tr className="border-t border-borda bg-superficie-suave font-semibold">
                {expand && <td className={cn(cell, grid)} />}
                {columns.map((c) => (
                  <td
                    key={c.title}
                    className={cn(
                      cell,
                      'text-texto',
                      grid,
                      ALIGN[c.alignment ?? 'centro'],
                    )}
                  >
                    {c.footer}
                  </td>
                ))}
                {trailing > 0 && <td colSpan={trailing} className={cn(cell, grid)} />}
              </tr>
            </tfoot>
          )}
        </table>
        {loading && <span className="sr-only">Carregando…</span>}
      </div>

      {/*
       * O paginador fica FORA do quadro que rola de lado.
       *
       * Dentro dele, os botões sairiam da vista junto com as colunas da
       * direita no telefone: quem precisasse virar página teria que rolar a
       * tabela até o fim para achar o botão.
       */}
      {paginate && (
        <Pagination
          page={current}
          perPage={perPage}
          total={ordered.length}
          onChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n);
            /* trocar a quantidade volta para a primeira página — ver `Pagination` */
            setPage(1);
          }}
          label={label.toLowerCase()}
          className="border-t border-borda px-5 py-4"
        />
      )}

      {/*
       * O painel de ações do desenho. Ele fica FORA do quadro que rola: um
       * diálogo renderizado dentro de um contêiner com `overflow-x` herda o
       * recorte dele em alguns navegadores.
       */}
      <ActionsModal
        open={actionsOf !== null}
        onOpenChange={(next) => !next && setActionsOf(null)}
        title={actionsOf === null ? label : (rowTitle?.(actionsOf) ?? label)}
        descricao={actionsOf === null ? undefined : rowSubtitle?.(actionsOf)}
        {...(rowIcon ? { icon: rowIcon } : {})}
        actions={actionsOf === null ? [] : (rowActions?.(actionsOf) ?? [])}
      />
    </>
  );
}
